/**
 * Payment Types for PayPal + Venmo Integration
 * Based on the PAYMENT document implementation plan
 */

import { Timestamp } from 'firebase/firestore';

// Checkout Status Flow: CREATED → ORDER_CREATED → CAPTURED → PAID
export type CheckoutStatus = 
  | 'CREATED'           // Initial checkout created
  | 'ORDER_CREATED'     // PayPal order created
  | 'CAPTURED'          // Payment captured (not yet confirmed)
  | 'PAID'              // Payment confirmed via webhook (source of truth)
  | 'FAILED'            // Payment failed
  | 'CANCELED';         // User canceled payment

export type PaymentCurrency = 'USD';

export type PaymentProvider = 'paypal' | 'venmo';

/**
 * Checkout document stored in Firestore: checkouts/{checkoutId}
 * This is the main payment tracking document
 */
export interface Checkout {
  // Identifiers
  id: string;
  bookingId?: string; // Link to booking if applicable
  
  // Status
  status: CheckoutStatus;
  
  // Amount (stored in cents to avoid floating point issues)
  amount: number; // e.g., 15000 for $150.00
  currency: PaymentCurrency;
  
  // PayPal specific data
  paypal: {
    orderId?: string;      // PayPal order ID
    captureId?: string;    // PayPal capture ID
    payerId?: string;      // PayPal payer ID
    provider?: PaymentProvider; // 'paypal' or 'venmo'
  };
  
  // Metadata for fulfillment
  metadata: {
    customerEmail?: string;
    customerName?: string;
    serviceType?: string;
    sessionDuration?: number;
    [key: string]: any; // Allow additional metadata
  };
  
  // Timestamps
  createdAt: Timestamp;
  updatedAt: Timestamp;
  paidAt?: Timestamp;
  
  // Error tracking
  errorMessage?: string;
  errorCode?: string;
}

/**
 * PayPal webhook event document: paypalWebhookEvents/{eventId}
 * Used for idempotency to prevent duplicate processing
 */
export interface PayPalWebhookEvent {
  eventId: string;           // PayPal event ID
  eventType: string;         // e.g., 'PAYMENT.CAPTURE.COMPLETED'
  checkoutId?: string;       // Linked checkout ID
  orderId?: string;          // PayPal order ID
  captureId?: string;        // PayPal capture ID
  receivedAt: Timestamp;
  processed: boolean;
  processingError?: string;
}

/**
 * PayPal Order creation request
 */
export interface CreateOrderRequest {
  checkoutId: string;
}

/**
 * PayPal Order creation response
 */
export interface CreateOrderResponse {
  orderId: string;
  status: string;
}

/**
 * PayPal Capture request
 */
export interface CaptureOrderRequest {
  checkoutId: string;
  orderId: string;
}

/**
 * PayPal Capture response
 */
export interface CaptureOrderResponse {
  captureId: string;
  captureStatus: string;
  amount: {
    value: string;
    currency: string;
  };
}

/**
 * PayPal API Order structure (simplified)
 */
export interface PayPalOrder {
  id: string;
  status: 'CREATED' | 'SAVED' | 'APPROVED' | 'VOIDED' | 'COMPLETED';
  intent: 'CAPTURE' | 'AUTHORIZE';
  purchase_units: Array<{
    reference_id?: string;
    custom_id?: string; // This is where we store checkoutId
    amount: {
      currency_code: string;
      value: string;
    };
    payee?: {
      email_address?: string;
      merchant_id?: string;
    };
    payments?: {
      captures?: Array<{
        id: string;
        status: string;
        amount: {
          currency_code: string;
          value: string;
        };
      }>;
    };
  }>;
  payer?: {
    payer_id?: string;
    email_address?: string;
    name?: {
      given_name?: string;
      surname?: string;
    };
  };
  create_time?: string;
  update_time?: string;
}

/**
 * PayPal Webhook payload structure
 */
export interface PayPalWebhookPayload {
  id: string; // Event ID
  event_type: string;
  event_version: string;
  create_time: string;
  resource_type: string;
  resource_version: string;
  resource: any; // The actual resource (order, capture, etc.)
  summary: string;
  links?: Array<{
    href: string;
    rel: string;
    method: string;
  }>;
}

/**
 * Client-side checkout creation request
 */
export interface CreateCheckoutRequest {
  amount: number; // in cents
  currency: PaymentCurrency;
  metadata: {
    customerEmail: string;
    customerName: string;
    serviceType: string;
    sessionDuration?: number;
    bookingId?: string;
    [key: string]: any;
  };
}

/**
 * Client-side checkout creation response
 */
export interface CreateCheckoutResponse {
  checkoutId: string;
  status: CheckoutStatus;
  amount: number;
  currency: PaymentCurrency;
}

/**
 * Payment status update event (for real-time listening)
 */
export interface PaymentStatusUpdate {
  checkoutId: string;
  status: CheckoutStatus;
  timestamp: Timestamp;
  errorMessage?: string;
}

// Type guard functions
export function isCheckoutPaid(checkout: Checkout): boolean {
  return checkout.status === 'PAID';
}

export function isCheckoutPending(checkout: Checkout): boolean {
  return ['CREATED', 'ORDER_CREATED', 'CAPTURED'].includes(checkout.status);
}

export function isCheckoutFailed(checkout: Checkout): boolean {
  return ['FAILED', 'CANCELED'].includes(checkout.status);
}

export function canRetryCheckout(checkout: Checkout): boolean {
  return ['CREATED', 'FAILED'].includes(checkout.status);
}

// Helper to convert dollars to cents
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}

// Helper to convert cents to dollars
export function centsToDollars(cents: number): number {
  return cents / 100;
}

// Helper to format amount for display
export function formatAmount(cents: number, currency: PaymentCurrency = 'USD'): string {
  const dollars = centsToDollars(cents);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(dollars);
}
